import {
  IonFab,
  IonFabButton,
  IonFabList,
  IonIcon,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonTitle,
  IonicSlides,
} from '@ionic/react';
import '@ionic/react/css/ionic-swiper.css';
import React, {
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router';
import { useMeasure } from 'react-use';
import { Swiper as SwiperInterface } from 'swiper';
import 'swiper/css';
import 'swiper/css/autoplay';
import 'swiper/css/keyboard';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import 'swiper/css/zoom';
import {
  Autoplay,
  Keyboard,
  Pagination,
  Scrollbar,
  Zoom,
} from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useHasPermission } from '../../api/authorization.provider';
import {
  Machine,
  MachineConnectionType,
  ScratchpadApp,
  useGetMachineQuery,
  useListScratchpadAppsQuery,
} from '../../api/graphql/generated';
import { BookmarkType } from '../../api/hooks/bookmark.hooks';
import {
  AppMode,
  useAppModeContext,
} from '../../components/app/app-mode-provider';
import { LoadingRetry } from '../../components/graphql/LoadingRetry';
import { NestedPageHeader } from '../../components/header/page.header';
import { newScratchpad } from '../../components/icons/new-scratch-pad';
import { MachineAuthorizationProvider } from '../../components/machine/machine-authorization-provider';
import { MachineConfig } from '../../components/machine/machine-config';
import { MachineProgramStatus } from '../../components/machine/machine-program-status';
import { ScratchpadPlaylistComponent } from '../../components/machine/playlist/scratchpad-playlist';
import { ScratchpadList } from '../../components/machine/scratchpad/scratchpad-list';
import {
  IonContentWithContext,
  useIonContentContext,
} from '../../components/ui/ion-content-with-context';
import { useBeaconRouter } from '../../route/route.hooks';
import { ScratchpadAppIcon } from '../../scratchpad/app-slugs';
import { useLocationHash, usePrevious } from '../../util/hooks';
import { MachineContextProvider } from './machine-context-provider';
import './machine.page.css';
import { RegisterMachinePage } from './register-machine.page';

interface RouteParams {
  id: string;
}

interface MachinePageProps {
  teamId: string;
}

interface MachineTopTab {
  hash: string;
  label: string;
  component: ReactElement;
}

const MachinePage = (props: MachinePageProps): ReactElement => {
  const params = useParams<RouteParams>();
  const { id } = params;

  return (
    <MachineAuthorizationProvider machineId={id} teamId={props.teamId}>
      <MachinePageInternal {...props} />
    </MachineAuthorizationProvider>
  );
};

const MachinePageInternal = (props: MachinePageProps): ReactElement => {
  const { teamId } = props;
  const { i18n } = useTranslation();

  const swiperRef = useRef<SwiperInterface>();
  const params = useParams<RouteParams>();
  const { id } = params;

  const hash = useLocationHash();
  const locationTab = hash?.replace('#', '');
  const [topTab, setTopTab] = useState<string | undefined>(locationTab);

  const { push } = useHistory();

  const canCreateScratchpad = useHasPermission('create', 'scratchpad', {
    machineId: id,
  });

  const {
    data: appsData,
    loading,
    error,
    refetch,
  } = useListScratchpadAppsQuery({
    variables: { input: { machineId: id } },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    onCompleted: () => {
      setReady(true);
    },
    skip: id === 'new',
  });

  const hasAppData: boolean = appsData?.listScratchpadApps ? true : false;
  const [ready, setReady] = useState<boolean>(hasAppData);

  const appsList = useMemo(
    () => appsData?.listScratchpadApps ?? [],
    [appsData]
  );

  const { data } = useGetMachineQuery({
    variables: { input: { id, teamId } },
    skip: id === 'new',
  });

  const { builder } = useBeaconRouter();

  const machine: Machine | undefined = data?.getMachine;

  const isDirectConnectMachine =
    machine?.connectionType === MachineConnectionType.DIRECT;

  const { mode } = useAppModeContext();
  const isDirectConnectMode = mode === AppMode.DIRECT;

  const MachineTopTab: Record<string, MachineTopTab> = useMemo(() => {
    return {
      PLAYLIST: {
        hash: 'playlist',
        label: i18n.t('common:machine_tab_playlist'),
        component: (
          <ScratchpadPlaylistComponent machine={machine} teamId={teamId} />
        ),
      },
      PARTS: {
        hash: 'parts',
        label: i18n.t('common:machine_tab_parts'),
        component: <ScratchpadList machine={machine} teamId={teamId} />,
      },
      CONFIGURE: {
        hash: 'config',
        label: i18n.t('common:machine_tab_config'),
        component: <MachineConfig machine={machine} />,
      },
    };
  }, [machine, teamId, i18n]);

  const tabs: MachineTopTab[] = useMemo(() => {
    const tabs = [MachineTopTab.CONFIGURE];

    // conditionally show parts/playlist based on scratchpad type
    if (appsList.length > 0) {
      tabs.unshift(MachineTopTab.PLAYLIST);
      tabs.unshift(MachineTopTab.PARTS);
    }
    return tabs;
  }, [
    MachineTopTab.CONFIGURE,
    MachineTopTab.PARTS,
    MachineTopTab.PLAYLIST,
    appsList.length,
  ]);

  const previousNumTabs = usePrevious(tabs.length);
  const numberTabsChanged = previousNumTabs !== tabs.length;
  const firstTabHash = tabs[0].hash;

  useEffect(() => {
    if (numberTabsChanged && !isDirectConnectMachine) {
      window.location.replace(`${window.location.pathname}#${firstTabHash}`);
    }
  }, [numberTabsChanged, firstTabHash, isDirectConnectMachine]);

  useEffect(() => {
    let initialTab;
    if (appsList.length === 0) {
      initialTab = MachineTopTab.CONFIGURE;
    } else {
      initialTab = MachineTopTab.PARTS;
    }

    const locationTabExists = tabs.some((tab) => tab.hash === locationTab);
    const assignLocationTab = locationTab && locationTabExists;
    const tab = assignLocationTab ? locationTab : initialTab.hash;

    if (tab && ready && tab !== topTab) {
      const speed = topTab === tab ? 0 : 250;
      setTopTab(tab);
      const index = tabs.findIndex((t) => t.hash === tab);
      swiperRef.current?.slideTo(index, speed);
    }
  }, [
    locationTab,
    appsList.length,
    ready,
    tabs,
    topTab,
    MachineTopTab,
    isDirectConnectMode,
    isDirectConnectMachine,
  ]);

  const onChangeSize = () => {
    swiperRef.current?.updateAutoHeight(0);
  };

  const createNewPart = (app: ScratchpadApp) => {
    const route = builder.getMachineScratchpadRoute(
      teamId,
      app.machineId,
      'new'
    );

    push(route, {
      app,
    });
  };
  const title = `${machine?.name} - ${i18n.t('common:page_team_machine')}`;

  if (id === 'new') {
    return <RegisterMachinePage teamId={teamId} />;
  } else {
    const scratchpadFabClassList: string[] = ['scratch-pad-fab'];
    if (topTab === MachineTopTab.PARTS.hash) {
      scratchpadFabClassList.push('show-fab');
    }

    const isMachineActive = machine?.active ? true : false;

    return (
      <MachineContextProvider machineId={id} teamId={teamId}>
        <IonPage>
          <NestedPageHeader
            title={title}
            defaultBackRoute={builder.getMachinesRoute(teamId)}
            secondaryToolbar={
              <IonSegment
                className="top-tabs"
                mode="md"
                scrollable={true}
                swipeGesture={false}
                value={topTab}
              >
                {generateTopTabs(tabs)}
              </IonSegment>
            }
            bookmarkProps={{
              enable: true,
              type: BookmarkType.MACHINE,
            }}
          >
            <IonTitle>
              {machine?.name}
              <div className="subtitle header-program-status">
                <MachineProgramStatus machine={machine} />
              </div>
            </IonTitle>
          </NestedPageHeader>
          <IonContentWithContext>
            <LoadingRetry
              loading={loading}
              refetch={refetch}
              error={error}
              results={{ hasResults: ready, noResultsComponent: null }}
            >
              <Swiper
                key={tabs.map((t) => t.hash).join('_')}
                onSwiper={(ref) => (swiperRef.current = ref)}
                modules={[
                  Autoplay,
                  Keyboard,
                  Pagination,
                  Scrollbar,
                  Zoom,
                  IonicSlides,
                ]}
                allowTouchMove={false}
                slidesPerView={1}
                autoHeight={true}
                pagination={false}
              >
                {tabs.map((tab, index) => {
                  return (
                    <SwiperSlide key={index}>
                      <SlideContents
                        onChangeSize={onChangeSize}
                        isActive={swiperRef.current?.activeIndex === index}
                      >
                        <>{tab.component}</>
                      </SlideContents>
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            </LoadingRetry>
            {canCreateScratchpad && isMachineActive && (
              <IonFab
                className={scratchpadFabClassList.join(' ')}
                vertical="bottom"
                horizontal="end"
                slot="fixed"
                data-cy="scratchpad-create"
              >
                <IonFabButton
                  onClick={() => {
                    if (appsList.length === 1) {
                      createNewPart(appsList[0] as ScratchpadApp);
                    }
                  }}
                >
                  <IonIcon icon={newScratchpad} />
                </IonFabButton>
                {appsList.length > 1 && (
                  <IonFabList side="top">
                    {appsList.map((a) => (
                      <IonFabButton
                        key={a.id}
                        onClick={() => {
                          createNewPart(a as ScratchpadApp);
                        }}
                      >
                        <IonIcon icon={ScratchpadAppIcon[a.slug]} />
                      </IonFabButton>
                    ))}
                  </IonFabList>
                )}
              </IonFab>
            )}
          </IonContentWithContext>
        </IonPage>
      </MachineContextProvider>
    );
  }
};

const generateTopTabs = (items: MachineTopTab[]): ReactElement[] => {
  return items.map((tab, index) => {
    return <Tab key={index} tab={tab} />;
  });
};

interface TabContext {
  isActive: boolean;
}

const TabContext = React.createContext<TabContext>({
  isActive: false,
});

export const useTabContext = (): TabContext => {
  const context = useContext(TabContext);
  return context as TabContext;
};

const Tab = ({ tab }: { tab: MachineTopTab }): ReactElement => {
  const buttonRef = useRef<HTMLIonSegmentButtonElement>(null);
  return (
    <IonSegmentButton
      ref={buttonRef}
      value={tab.hash}
      onClick={() => {
        window.location.replace(`${window.location.pathname}#${tab.hash}`);
        buttonRef.current?.scrollIntoView({ inline: 'end' });
      }}
    >
      {tab.label}
    </IonSegmentButton>
  );
};

interface SlideContentsProps {
  children: ReactNode;
  onChangeSize: () => void;
  isActive: boolean;
}

const SlideContents = (props: SlideContentsProps): ReactElement => {
  const { onChangeSize, children, isActive } = props;
  const priorHeightRef = useRef<number>();
  const [ref, { height }] = useMeasure<HTMLDivElement>();

  useEffect(() => {
    if (priorHeightRef.current !== height) {
      priorHeightRef.current = height;
      onChangeSize();
    }
  }, [height, onChangeSize]);

  const contentContext = useIonContentContext();

  return (
    <TabContext.Provider value={{ isActive }}>
      <div
        ref={ref}
        className={'machine-slide max-content-width'}
        style={{ minHeight: contentContext?.height }}
      >
        {children}
      </div>
    </TabContext.Provider>
  );
};

export { MachinePage };
